import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import currency from 'currency.js';
import Highcharts from 'highcharts';

// colors: #f0ad4e (orange) | #0b68a1 (blue) | #d0eafb (light blue)
interface ComponentArgs {
  firstPercent?: number;
  firstAmount?: number;
  firstColor?: string;
  secondPercent?: number;
  secondAmount?: number;
  secondColor?: string;
  totalPercentColor?: string; // if null then uses args.secondColor
  totalPercentFontSize?: string; // if null then 2rem;
  leftToRaiseAmount?: number;
  hideAmount?: boolean; // default false
  onClick?: () => void;
}

export default class DashboardDonutChartSplit extends Component<ComponentArgs> {
  @service declare formatAmountService: any;

  chartElement?: undefined;
  @tracked tooltipLabel?: string;
  @tracked tooltipAmount?: string;
  @tracked tooltipPercent?: string;

  get firstPercent() {
    return this.args.firstPercent ?? 0;
  }

  get firstAmount() {
    return this.args.firstAmount ?? 0;
  }

  get firstColor() {
    return this.args.firstColor;
  }

  get secondPercent() {
    return this.args.secondPercent ?? 0;
  }

  get secondAmount() {
    return this.args.secondAmount ?? 0;
  }

  get secondColor() {
    return this.args.secondColor;
  }

  get totalPercentColor() {
    return this.args.totalPercentColor || this.secondColor;
  }

  get totalPercentFontSize() {
    return this.args.totalPercentFontSize ?? '2rem';
  }

  get hideAmount() {
    return this.args.hideAmount ?? false;
  }

  get leftToRaisePercent() {
    let percent = 100 - this.firstPercent - this.secondPercent;
    if (percent < 0) {
      percent = 0;
    }
    return percent;
  }

  get totalPercent() {
    return this.firstPercent + this.secondPercent;
  }

  get totalAmount(): number {
    return currency(this.firstAmount).add(this.secondAmount).value;
  }

  get chartSettings() {
    let {
      firstPercent,
      firstAmount,
      secondPercent,
      secondAmount,
      leftToRaisePercent,
    } = this;
    let self = this;
    if (firstPercent > 100) {
      firstPercent = 100;
    }
    if (firstPercent + secondPercent > 100) {
      secondPercent = 100 - firstPercent;
    }
    let fillerPercent = 100 - firstPercent - secondPercent;

    let titleText = `<span>${this.totalPercent}%</span>`;
    if (!this.hideAmount) {
      let formattedAmount = this.formatAmountService.format(this.totalAmount, {
        roundToDollar: true,
      });
      titleText = `${titleText}<div style='font-size: 1rem; color: #636c72'>${formattedAmount}</div>`;
    }

    return {
      chart: {
        type: 'pie',
        spacing: [0, 0, 0, 0],
      },
      title: {
        text: titleText,
        align: 'center',
        verticalAlign: 'middle',
        useHTML: true,
        style: {
          fontSize: this.totalPercentFontSize,
          color: this.totalPercentColor,
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        },
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        pie: {
          shadow: false,
          center: ['50%', '50%'],
        },
      },
      tooltip: {
        formatter: function () {
          if (self.tooltipLabel !== this.point.name) {
            self.tooltipLabel = this.point.name;
            self.tooltipAmount = this.point.amount;
            self.tooltipPercent = this.point.percent;
          }
          return false;
        },
      },
      series: [
        {
          size: '100%',
          innerSize: '90%',
          dataLabels: {
            enabled: false,
          },
          data: [
            {
              name: 'Given',
              y: firstPercent,
              color: this.firstColor,
              percent: firstPercent,
              amount: firstAmount,
            },
            {
              name: 'Expecting',
              y: secondPercent,
              color: this.secondColor,
              percent: secondPercent,
              amount: secondAmount,
            },
            {
              name: 'Left to Raise',
              y: fillerPercent,
              color: '#eceeef',
              percent: leftToRaisePercent,
              amount: this.args.leftToRaiseAmount,
            },
          ],
        },
      ],
    };
  }

  @action
  handleClick() {
    this.args.onClick?.();
  }

  @action
  handleMouseout() {
    if (this.tooltipLabel !== undefined) {
      this.tooltipLabel = undefined;
    }
  }

  @action
  renderChart(el: any) {
    this.chartElement = el;
    Highcharts.chart(el, this.chartSettings);
  }
}
